// assets/media-library.js

(() => {
  'use strict';

  class MediaLibrary {
    constructor(root) {
      this.root = root;

      this.cards = Array.from(
        root.querySelectorAll('.media-card')
      );

      this.filterType = root.querySelector(
        '[data-media-filter="type"], #filter-type'
      );

      this.filterCategory = root.querySelector(
        '[data-media-filter="category"], #filter-category'
      );

      this.downloadButton = root.querySelector(
        '.download-selected'
      );

      this.selectedCount = root.querySelector(
        '.selected-count'
      );

      this.modal = root.querySelector(
        '.media-preview-modal'
      );

      this.previewContainer = this.modal?.querySelector(
        '.media-preview-modal__preview'
      );

      this.loadingElement = this.modal?.querySelector(
        '.media-preview-modal__loading'
      );

      this.lastFocusedElement = null;
      this.selectedFiles = new Set();

      if (
        !this.cards.length ||
        !this.downloadButton ||
        !this.selectedCount
      ) {
        return;
      }

      this.bindEvents();
      this.updateSelectionState();
      this.initializeFileSizes();
    }

    bindEvents() {
      this.filterType?.addEventListener('change', () => {
        this.applyFilters();
      });

      this.filterCategory?.addEventListener('change', () => {
        this.applyFilters();
      });

      this.root.addEventListener('change', (event) => {
        const checkbox = event.target.closest('.media-checkbox');

        if (checkbox) {
          this.handleCheckboxChange(checkbox);
        }
      });

      this.root.addEventListener('click', (event) => {
        const previewButton = event.target.closest(
          '.media-card__preview-button'
        );

        if (previewButton) {
          event.preventDefault();
          this.showPreview(previewButton);
          return;
        }

        const closeButton = event.target.closest(
          '.media-preview-modal__close'
        );

        const clickedOverlay = event.target.classList.contains(
          'media-preview-modal__overlay'
        );

        if (closeButton || clickedOverlay) {
          event.preventDefault();
          this.hidePreview();
        }
      });

      this.downloadButton.addEventListener('click', () => {
        this.handleDownload();
      });

      this.handleKeydown = (event) => {
        if (
          event.key === 'Escape' &&
          this.modal?.getAttribute('aria-hidden') === 'false'
        ) {
          this.hidePreview();
        }
      };

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );
    }

    handleCheckboxChange(checkbox) {
      const fileUrl = checkbox.dataset.fileUrl;

      if (!fileUrl) {
        checkbox.checked = false;
        return;
      }

      if (checkbox.checked) {
        this.selectedFiles.add(fileUrl);
      } else {
        this.selectedFiles.delete(fileUrl);
      }

      this.updateSelectionState();
    }

    updateSelectionState() {
      const count = this.selectedFiles.size;

      this.selectedCount.textContent = String(count);

      this.downloadButton.disabled = count === 0;

      this.downloadButton.setAttribute(
        'aria-disabled',
        count === 0 ? 'true' : 'false'
      );
    }

    applyFilters() {
      const type = this.filterType?.value || 'all';
      const category = this.filterCategory?.value || 'all';

      this.cards.forEach((card) => {
        const matchesType =
          type === 'all' ||
          card.dataset.mediaType === type;

        const matchesCategory =
          category === 'all' ||
          card.dataset.mediaCategory === category;

        card.classList.toggle(
          'filtered',
          !(matchesType && matchesCategory)
        );
      });
    }

    async showPreview(button) {
      if (
        !this.modal ||
        !this.previewContainer ||
        !this.loadingElement
      ) {
        return;
      }

      const url = button.dataset.previewUrl;

      if (!url) {
        this.showNotification(
          'No preview URL has been supplied.',
          'error'
        );

        return;
      }

      this.lastFocusedElement = button;

      document.documentElement.classList.add(
        'overflow-hidden'
      );

      document.body.style.overflow = 'hidden';

      this.modal.setAttribute(
        'aria-hidden',
        'false'
      );

      this.showLoading();

      try {
        const fileType = this.getFileType(url);

        let content;

        switch (fileType) {
          case 'pdf':
            content = `
              <iframe
                src="${this.escapeAttribute(url)}"
                title="Media preview"
                width="100%"
                height="100%"
              ></iframe>
            `;
            break;

          case 'mp4':
          case 'webm':
          case 'mov':
            content = `
              <video controls playsinline>
                <source
                  src="${this.escapeAttribute(url)}"
                  type="${this.getVideoMimeType(fileType)}"
                >

                Your browser does not support this video.
              </video>
            `;
            break;

          case 'jpg':
          case 'jpeg':
          case 'png':
          case 'gif':
          case 'webp':
          case 'avif': {
            const image = await this.loadImage(url);

            image.alt =
              button
                .getAttribute('aria-label')
                ?.replace(/^Preview\s*/i, '') || '';

            this.previewContainer.replaceChildren(image);

            this.hideLoading();

            this.modal
              .querySelector('.media-preview-modal__close')
              ?.focus();

            return;
          }

          default:
            content = `
              <p>
                Preview is not available for this file type.
              </p>
            `;
        }

        this.previewContainer.innerHTML = content;
      } catch (error) {
        console.error(
          'Media preview failed:',
          error
        );

        this.previewContainer.innerHTML = `
          <p>Error loading preview.</p>
        `;
      }

      this.hideLoading();

      this.modal
        .querySelector('.media-preview-modal__close')
        ?.focus();
    }

    hidePreview() {
      if (
        !this.modal ||
        this.modal.getAttribute('aria-hidden') === 'true'
      ) {
        return;
      }

      const video = this.previewContainer?.querySelector(
        'video'
      );

      if (video) {
        video.pause();
      }

      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );

      this.previewContainer?.replaceChildren();

      document.documentElement.classList.remove(
        'overflow-hidden'
      );

      document.body.style.overflow = '';

      this.lastFocusedElement?.focus();

      this.lastFocusedElement = null;
    }

    showLoading() {
      if (
        !this.loadingElement ||
        !this.previewContainer
      ) {
        return;
      }

      this.loadingElement.style.display = 'block';
      this.previewContainer.style.display = 'none';
    }

    hideLoading() {
      if (
        !this.loadingElement ||
        !this.previewContainer
      ) {
        return;
      }

      this.loadingElement.style.display = 'none';
      this.previewContainer.style.display = 'block';
    }

    getFileType(url) {
      try {
        const pathname = new URL(
          url,
          window.location.href
        ).pathname;

        return (
          pathname
            .split('.')
            .pop()
            ?.toLowerCase() || ''
        );
      } catch (error) {
        return (
          url
            .split('?')[0]
            .split('#')[0]
            .split('.')
            .pop()
            ?.toLowerCase() || ''
        );
      }
    }

    getVideoMimeType(fileType) {
      if (fileType === 'mov') {
        return 'video/quicktime';
      }

      return `video/${fileType}`;
    }

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = url;
      });
    }

    escapeAttribute(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    }

    async handleDownload() {
      if (!this.selectedFiles.size) {
        return;
      }

      const buttonText =
        this.downloadButton.querySelector(
          '.button__text'
        );

      const loadingIndicator =
        this.downloadButton.querySelector(
          '.button__loading-indicator'
        );

      buttonText?.classList.add('hidden');

      loadingIndicator?.classList.remove(
        'hidden'
      );

      this.downloadButton.disabled = true;

      this.downloadButton.setAttribute(
        'aria-busy',
        'true'
      );

      this.showNotification(
        'Starting download…'
      );

      let failedDownloads = 0;

      for (const fileUrl of this.selectedFiles) {
        const filename = this.getFilename(fileUrl);

        try {
          const response = await fetch(fileUrl);

          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}`
            );
          }

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');

          link.href = objectUrl;
          link.download = filename;
          link.hidden = true;

          document.body.appendChild(link);

          link.click();
          link.remove();

          window.setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
          }, 1000);
        } catch (error) {
          failedDownloads += 1;

          console.error(
            `Download failed for ${fileUrl}:`,
            error
          );
        }
      }

      if (failedDownloads) {
        this.showNotification(
          `${failedDownloads} file${
            failedDownloads === 1 ? '' : 's'
          } could not be downloaded.`,
          'error'
        );
      } else {
        this.showNotification(
          'Download complete!',
          'success'
        );
      }

      this.root
        .querySelectorAll('.media-checkbox')
        .forEach((checkbox) => {
          checkbox.checked = false;
        });

      this.selectedFiles.clear();

      buttonText?.classList.remove('hidden');

      loadingIndicator?.classList.add(
        'hidden'
      );

      this.downloadButton.removeAttribute(
        'aria-busy'
      );

      this.updateSelectionState();
    }

    getFilename(url) {
      try {
        const pathname = new URL(
          url,
          window.location.href
        ).pathname;

        return (
          decodeURIComponent(
            pathname.split('/').pop()
          ) || 'download'
        );
      } catch (error) {
        return (
          url
            .split('?')[0]
            .split('/')
            .pop() || 'download'
        );
      }
    }

    showNotification(message, type = 'info') {
      this.root
        .querySelector('.download-notification')
        ?.remove();

      const notification =
        document.createElement('div');

      notification.className =
        `download-notification ${type}`;

      notification.setAttribute(
        'role',
        type === 'error' ? 'alert' : 'status'
      );

      notification.setAttribute(
        'aria-live',
        type === 'error'
          ? 'assertive'
          : 'polite'
      );

      notification.textContent = message;

      this.root.appendChild(notification);

      requestAnimationFrame(() => {
        notification.classList.add('active');
      });

      window.setTimeout(() => {
        notification.classList.remove('active');

        window.setTimeout(() => {
          notification.remove();
        }, 300);
      }, 3000);
    }

    formatFileSize(bytes) {
      if (
        !Number.isFinite(bytes) ||
        bytes <= 0
      ) {
        return 'Unknown size';
      }

      const units = [
        'B',
        'KB',
        'MB',
        'GB'
      ];

      const unitIndex = Math.min(
        Math.floor(
          Math.log(bytes) / Math.log(1024)
        ),
        units.length - 1
      );

      const value =
        bytes / Math.pow(1024, unitIndex);

      return `${parseFloat(
        value.toFixed(2)
      )} ${units[unitIndex]}`;
    }

    async fetchFileSize(url) {
      try {
        const response = await fetch(url, {
          method: 'HEAD'
        });

        if (!response.ok) {
          return null;
        }

        const size = Number(
          response.headers.get('content-length')
        );

        if (
          !Number.isFinite(size) ||
          size <= 0
        ) {
          return null;
        }

        return this.formatFileSize(size);
      } catch (error) {
        return null;
      }
    }

    async initializeFileSizes() {
      await Promise.all(
        this.cards.map(async (card) => {
          const checkbox = card.querySelector(
            '.media-checkbox'
          );

          const fileUrl =
            checkbox?.dataset.fileUrl;

          const sizeElement =
            card.querySelector('.file-size');

          if (!fileUrl || !sizeElement) {
            return;
          }

          const fileSize =
            await this.fetchFileSize(fileUrl);

          if (fileSize) {
            sizeElement.textContent =
              fileSize;
          }
        })
      );
    }
  }

  function initializeMediaLibraries(
    container = document
  ) {
    const sectionRoots = [];

    if (
      container.matches?.('.shopify-section') &&
      container.querySelector('.media-library')
    ) {
      sectionRoots.push(container);
    }

    container
      .querySelectorAll?.('.shopify-section')
      .forEach((section) => {
        if (
          section.querySelector('.media-library')
        ) {
          sectionRoots.push(section);
        }
      });

    sectionRoots.forEach((root) => {
      if (
        root.dataset.mediaLibraryInitialized ===
        'true'
      ) {
        return;
      }

      root.dataset.mediaLibraryInitialized =
        'true';

      new MediaLibrary(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeMediaLibraries();
      }
    );
  } else {
    initializeMediaLibraries();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeMediaLibraries(event.target);
    }
  );
})();